/**
 * Capability matrix — frontend mirror (Phase 1.11).
 *
 * Drives affordance gating in the SPA: which buttons render, which inline-
 * edit primitives accept edits vs render read-only, which tabs the
 * AppShell shows. Server-side gates at api/_shared/capabilities.ts are
 * the authoritative authorization layer; this file is the affordance-
 * level mirror so users don't see buttons that always 403.
 *
 * §m — frontend mirror of api/_shared/capabilities.ts. Phase 1 boundary
 * discipline avoids cross-subproject imports between api/ and src/. If
 * `ROLE_CAPABILITIES` changes server-side, update the matrix here in
 * lockstep. No test catches this drift; the §m discipline is the binding.
 *
 * **Post-Phase 1.11**: this file is the SINGLE frontend mirror of the
 * matrix. It replaced prior scattered mirrors at:
 *   - src/lib/auth.ts:WRITE_ROLES_FOR_ARCHIVE (archive and restore, now
 *     canArchiveProviders)
 *   - src/components/AppShell.tsx:WRITE_ROLES (canSubmitSuggestions;
 *     routed through canSuggest)
 *   - src/lib/lookups.ts:DATA_STEWARD_ROLES (canAddLookup; routed
 *     through canManageLookups)
 * All three consolidated; no other frontend WRITE_ROLES /
 * DATA_STEWARD_ROLES / OWNER_ROLES mirror sites exist. If you find
 * yourself adding a hardcoded role list elsewhere in src/, add a
 * predicate to this file and import it instead — the §m discipline is
 * the binding (no test catches drift between the server matrix at
 * api/_shared/capabilities.ts:ROLE_CAPABILITIES and the mirror below).
 */

import type { WhoamiResponse } from './types';
import { isConnectedClientsFenced, isInternalStaffRole } from './clientRecordAccess';

// Mirrors api/_shared/schema.ts:125 UserTenantRole.
export type UserTenantRole =
  | 'primary_cm'
  | 'supervising_cm'
  | 'admin_support'
  | 'owner'
  | 'mso_staff'
  | 'data_steward'
  // Phase X.5.1 (migration 0083) — exec-tier hub/tool access (Laila).
  | 'exec_support'
  // Board observer (migration 0152) — read-only board-member role.
  | 'board_observer'
  // The Directory Editor: edits the directory, no client records by default.
  | 'directory_editor'
  // Accounting (0220): billing, payroll hours and reports; no client record.
  | 'accounting';

// Mirrors api/_shared/capabilities.ts CAPABILITIES tuple.
export const CAPABILITIES = [
  'read',
  'direct_write',
  'suggest',
  'add_notes',
  'approve',
  'read_all_suggestions',
  'manage_lookups',
  'manage_team',
  // See what failed: Admin's Errors tab (ADR-052), every failure with who,
  // when, where and the reference to quote. The office seats: owner, data
  // steward, admin support and exec support (the admin support floor).
  'view_errors',
  // Onboard or offboard a person, in one act: add them to the roster or
  // remove them, and grant, change, or revoke their sign-in and role.
  // Split off manage_team so exec_support can do this without the
  // owner-only AI caps/prompts floor.
  'manage_staff_access',
  'recommend_provider',
  'delete_provider_note',
  'merge_providers',
  // Take an organization or person out of the directory and bring one
  // back (the archive and restore routes, and is_active on the PATCH
  // routes), and remove a document someone else attached. Split off
  // direct_write so a seat can edit listings without removing them.
  'archive_providers',
  // See the directory entries that could not be placed on the map (the two
  // Not placed lists under it): the people who fix directory addresses.
  // A decluttering gate in the browser, not a data boundary: the map read
  // still carries them for the pins and the search.
  'view_directory_gaps',
  // Phase X.0 — Operations Dashboard (the 8 view_dashboard_* caps were
  // removed 2026-07-18 with the retired dashboard read chain)
  'manage_staff_availability',
  // Phase X.3 — CM AI Tools Portal
  'use_ai_tools',
  // Phase X.5.1 — Olive hub: 3 tier caps + announcements mgmt
  'view_staff_tools',
  'view_office_tools',
  'view_exec_tools',
  'manage_announcements',
  // Billing A4 — invoice compile / state machine / issue / void / preview
  'compile_invoice',
  'approve_invoice',
  'issue_invoice',
  'void_invoice',
  'view_invoice',
  // Open a client's Pre-billing tab and read its month: every case note's
  // hours, band, invoice line and state. Rates and amounts need view_invoice;
  // a seat without it reads the lines with no money in them.
  'view_prebilling',
  // The master Pre-billing page: every client's note lines for a date
  // range, with who still holds drafts. Held by the office and the
  // accountants; amounts on it also need view_invoice.
  'view_master_prebilling',
  // Client rates (migration 0132, B1-D3) — set/change a client's per-band
  // billing rates (owner + the bookkeeping seats; founder ruling 2026-07-07)
  'manage_client_rates',
  // Choose who receives a client's bill: mark a contact as the billing
  // contact, add or change the Billing details, and refresh them from the
  // contact they came from. Rates stay on manage_client_rates.
  'set_bill_to',
  // Workspace v1 (migration 0098) — office + manager work module
  'view_workspace',
  'manage_work_items',
  'manage_checklist_templates',
  // Card notes — edit or delete ANYONE's note on a Workspace card (the
  // author always may their own; manage_work_items alone covers that).
  'moderate_work_item_notes',
  // Workspace v2 Slice 3c (migration 0107) — edit CM bio + photo (People tab)
  'manage_staff_profiles',
  // Clinical substrate (ADR-043) — all-clients clinical width; drives the
  // app.clinical_scope GUC in setTenantContext. Default-deny (gate cond. C2).
  'view_all_clinical',
  // The engagement lane: a line clinician opens every client in the
  // service she does. With staff_roster.does_care_management the lane is
  // every care-management client; with does_spice every SPICE client; with
  // both flags every engaged client; with neither flag no lane, and her
  // care teams alone apply. When the tenant's client record setting is
  // all_staff, an internal holder reads every client instead
  // (clinicalScopeFor). Reads and writes widen together
  // (clinical_scope_allows, ADR-0053). Office seats hold view_all_clinical
  // and never need this.
  'view_engagement_clients',
  // Kith clinical record surface — open to all internal roles per CEO
  // directive (fine-grained per-role restriction lands later via a runtime
  // owner Permissions screen).
  'read_clinical_record',  // may open a client's clinical record surface (notes feed, proposals queue, record projections)
  'write_clinical_notes',  // may author into the clinical record (post notes, edit own billing projection, confirm/dismiss proposals)
  // Change or remove a note someone else wrote on a client's record (the author always may her own). Never widens which clients a person can open: row scope stays clinical_scope_allows.
  'moderate_clinical_notes',
  // Board demo sandbox (migration 0156; build-specs/board-demo-sandbox.md) —
  // a DEMO-SCOPED clinical write cap granted ONLY to board_observer. The
  // note-pipeline write handlers admit write_clinical_notes OR this; when a
  // caller holds only this, the repo fence (_clinicalWriteGuard) allows the
  // write ONLY against a client flagged is_demo=true, so board members can
  // experience the note pipeline on the demo client (Frodo) without ever
  // touching a real record. Full clinical write (write_clinical_notes) stays
  // FORBIDDEN for board_observer.
  'write_demo_clinical',
  // Board observer (migration 0152; build-specs/board-observer-role.md) —
  // the client-identity WRITE gate, split off view_all_clinical (which now
  // gates all-clients READ only). Granted to the five office roles; a
  // board_observer holds view_all_clinical but NOT this, so it reads every
  // record and writes none. Re-gates: clients.ts identity writes, client
  // addresses/photos writes, and the billing-contact refresh.
  'manage_client_identity',
  // Change a client's care manager, or add or remove another, on a client
  // the caller may open (N4). Narrower than manage_client_identity, which
  // also renames, archives and edits a client: this is the care-manager
  // slots only, never a SPICE therapist. Held by the line care managers
  // and the five office roles.
  'assign_care_managers',
  // A client's everyday details, split from manage_client_identity: phone,
  // email, preferred name, pronouns, photo, relationship status, referred
  // by, the service and its SPICE therapist, additional SPICE staff and the
  // SPICE schedule, on a client the caller may open. Held by the six
  // internal staff roles; manage_client_identity keeps adding, archiving,
  // renaming, status, date of birth, gender and addresses.
  'edit_client_details',
  // Reports Phase A (#515) — referral-partner analytics. Owner-only at
  // launch (founder 2026-07-13); widened to owner + board_observer by 0152
  // (the read-only board seat sees the reports pages).
  'view_reports',
  // Reports Phase B — weekly operating metrics ingest (0151). A SEPARATE
  // owner-only WRITE capability, so the ingest gate never rides view_reports
  // (which widens later): POST /reports/operating/weekly gates on this;
  // the GET read path gates on view_reports.
  'ingest_operating_metrics',
  // Referral-report split (founder ruling 2026-07-14) — GET
  // /v1/reports/referrals/weekly now gates on this SEPARATE capability
  // instead of view_reports, so data_steward + exec_support can see the
  // referral-partner analytics without gaining the operating dashboard
  // (which stays view_reports, owner + board_observer only). Granted to
  // owner, board_observer, data_steward, exec_support.
  'view_referral_reports',
  // Editable workspace processes (migration 0154) — process + stage CRUD
  // (multi-process kanbans). Granted owner + data_steward + exec_support
  // (founder ruling 2026-07-14). Checklist-template item CRUD stays on the
  // existing manage_checklist_templates (which now also grants exec_support).
  'manage_workspace_processes',
  // RBAC Permissions screen (ADR-0046, migration 0157) — the owner Admin
  // matrix that edits per-tenant role-capability overrides. Owner-only and
  // HARD-LOCKED: structurally un-grantable and un-revokable (the overrides
  // repo refuses writes touching it, and the owner row short-circuits to
  // the static matrix on the read path), so no self-lockout path exists.
  'manage_permissions',
  // QBO billing integration (QBO API v2 slice 5) — DISCONNECT QuickBooks:
  // revoking the practice's long-lived credential to its books, and the
  // only act that makes room to connect a different company. Deliberately
  // NOT manage_client_rates (that cap is wide on purpose: the bookkeeping
  // seats set prices INSIDE Olive). Owner-only — the
  // ingest_operating_metrics precedent of a separate owner-only WRITE
  // cap. Connect and reconnect ride connect_billing_integration, the push
  // rides push_invoice, the customer links ride manage_qbo_customer_links,
  // and the push-STATUS read rides view_invoice so the office seat can
  // SEE a failed push without being able to cause one.
  'manage_billing_integration',
  // Connect QuickBooks, and RECONNECT it when the credential expires.
  //
  // SPLIT OUT of manage_billing_integration for the same reason
  // push_invoice was: the owner-only argument above is about REVOKING
  // access to the books and about which company Olive points at, and a
  // reconnect changes neither. The connect callback refuses a consent
  // for a different company than the one connected before it spends the
  // authorization code, so a connect can only re-authorize the connected
  // company, or connect one when none is; pointing Olive at another
  // company still requires a disconnect first, and disconnect stays on
  // manage_billing_integration. Bundled, an expired token stopped month
  // end until the owner was free; the seat that runs month end brings
  // the connection back itself.
  //
  // Held by owner + exec_support, beside manage_billing_integration on
  // the owner row exactly as push_invoice sits beside it.
  'connect_billing_integration',
  // Push an ISSUED invoice into QuickBooks (founder ruling 2026-08-26:
  // "laila is exec_support - she should be able to issue and push").
  //
  // SPLIT OUT of manage_billing_integration, which used to carry connect,
  // disconnect AND push as one authority. That bundling was the defect.
  // The owner-only argument above is entirely about CONNECT and
  // DISCONNECT: those hand Intuit's consent screen to a human and what
  // comes back is a long-lived credential to the practice's books. It was
  // never an argument about SENDING a document the practice already
  // decided to issue. Bundled, it made the founder the only person who
  // could run month end, at a company where the bookkeeper does the
  // billing. Connect and disconnect keep the original posture for the
  // original reason; only the operational verb moves.
  'push_invoice',
  // Bind a client to a QuickBooks CUSTOMER, and unbind one (founder ruling
  // 2026-08-26: "onboarding a new client can't just route through me, it
  // needs to also be exec_support").
  //
  // The second verb to leave manage_billing_integration, for the same
  // reason push_invoice did: linking is the billing operator's work, not
  // an authorization act against Intuit. Onboarding a client is a weekly
  // job at a growing practice; routing it through one person makes that
  // person the bottleneck on new revenue.
  //
  // SEPARATE FROM push_invoice even though the holders are identical
  // today, because the objects and the blast radii differ: a failed push
  // is visible and retryable, a WRONG LINK quietly bills one family's
  // hours to another family's QuickBooks customer. Keeping it its own cell
  // means that question can be narrowed later without a migration.
  //
  // NOT manage_client_identity, which FIVE roles hold (owner,
  // supervising_cm, admin_support, data_steward, exec_support) — a
  // supervising CM has no business binding clients to an external
  // accounting system.
  'manage_qbo_customer_links',
  // Team directory — the private-contact tier (home address, personal
  // phone). Every internal seat reads the directory at the view_staff_tools
  // floor; only holders of this capability receive the two private columns
  // (repos/staff.ts nulls them server-side for everyone else).
  'view_staff_private_contact',
  // Internal time: log one's own admin, training and meeting hours, the
  // hours that feed pay but never a client's bill. Every roster person
  // with a login holds it; the external and board seats do not.
  'log_internal_time',
  // Download the pay-period hours file (billable hours from notes plus
  // internal hours) that the office feeds to payroll. Hours only: rates
  // and pay stay office-side. The owner and the payroll seat.
  'export_payroll_hours',
  // A person's own to-do list: create, edit, complete, archive and delete
  // tasks they own and are assigned to; never an intake card, a process
  // item, or anyone else's task; no board. A task is theirs when it is
  // assigned to them; archive and delete also need them to have made it,
  // so the office's task assigned to them can be completed, not removed.
  // The care managers and SPICE therapists hold it by default. Every seat
  // with manage_work_items already covers its own tasks and does not need
  // it. Routes: POST/PATCH/archive/DELETE/complete-recurring on work-items
  // and the detail read, each with a 'manage_work_items+manage_own_tasks'
  // (or 'view_workspace+manage_own_tasks') door; the repos apply the
  // scope to the loaded row in the same transaction as the write. LP-16:
  // removing a collaborator takes the same door, for the caller's own row
  // only (leaving a card shared with them).
  'manage_own_tasks',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

/**
 * Role × capability matrix. Verbatim mirror of api/_shared/capabilities.ts
 * `ROLE_CAPABILITIES`. Order of keys matches the server file for
 * diff-friendly review.
 *
 * Phase 1.11 launch deltas:
 *   - primary_cm / supervising_cm / admin_support — gained `add_notes` as
 *     a first-class capability (previously bundled into `direct_write`).
 *     LOST `direct_write` per Amanda 2026-05-14. KEEP `suggest`.
 *   - owner / data_steward — unchanged authority surface.
 *   - mso_staff — unchanged (read-only; Phase 7 introduces write surface
 *     via service_engagement).
 */
export const ROLE_CAPABILITIES: Record<UserTenantRole, ReadonlySet<Capability>> = {
  primary_cm: new Set<Capability>([
    'read', 'suggest', 'add_notes',
    'use_ai_tools',  // X.3 — all 15 CMs get the portal
    'view_staff_tools',  // X.5.1 — hub Level 1 (internal staff tiles)
    // Kith clinical record — open to all internal roles (CEO directive).
    // primary_cm gets read + write but NOT view_all_clinical; her reach is
    // view_engagement_clients below.
    'read_clinical_record',
    'write_clinical_notes',
    // Pre-billing's case-note lines, with no rate or amount (no view_invoice).
    'view_prebilling',
    // The engagement lane: every client in the service the roster flags
    // say she does (see the CAPABILITIES comment).
    'view_engagement_clients',
    // N4: change a client's care manager, or add or remove one.
    'assign_care_managers',
    'edit_client_details',
    // Internal time: her own admin, training and meeting hours.
    'log_internal_time',
    // Her personal to-do list in the Workspace, without the office's boards.
    'manage_own_tasks',
  ]),
  supervising_cm: new Set<Capability>([
    'read', 'suggest', 'add_notes',
    'use_ai_tools',                      // X.3 — Marisa / Starr / Jackie
    'view_staff_tools',                  // X.5.1 — hub Level 1
    'view_office_tools',                 // X.5.1 — hub Level 2 (office tiles)
    // Workspace v1 — managers run the board (not template authoring).
    'view_workspace',
    'manage_work_items',
    // Workspace v3.x Brief 3 — supervisors maintain their CMs' bios + photos
    // (the Assigned-stage "photo + bio" step). Bio/photo only; never manage_team.
    'manage_staff_profiles',
    // Kith clinical record — open to all internal roles (CEO directive).
    // Supervisors get read + write + all-clients width (CEO-confirmed:
    // supervising CMs see all clients).
    'read_clinical_record',
    'write_clinical_notes',
    // Pre-billing's case-note lines, with no rate or amount (no view_invoice).
    'view_prebilling',
    'view_all_clinical',
    // Board observer split (0152) — the client-identity write gate (the
    // office five hold it; behavior unchanged for them).
    'manage_client_identity',
    'assign_care_managers',
    'edit_client_details',
    // Internal time: her own admin, training and meeting hours.
    'log_internal_time',
  ]),
  admin_support: new Set<Capability>([
    'view_master_prebilling',
    'read', 'suggest', 'add_notes',
    // Directory notes: delete anyone's note, like the rest of the office.
    'delete_provider_note',
    'manage_staff_availability',           // Nicole runs Friday standups
    'use_ai_tools',                        // X.3 — Nicole (Laila → exec_support at X.5.1)
    'view_staff_tools',                    // X.5.1 — hub Level 1
    'view_office_tools',                   // X.5.1 — hub Level 2 (office tiles)
    'view_invoice',                        // Billing A4 — invoice preview only
    'set_bill_to',                         // who receives the bill
    'view_prebilling',                    // Pre-billing's case-note lines
    // Workspace v1 — office works the pipeline (not template authoring).
    'view_workspace',
    'manage_work_items',
    // Workspace v2 Slice 3c — maintain CM bios + photos (People tab).
    'manage_staff_profiles',
    // Kith clinical record — open to all internal roles (CEO directive):
    // read + write + all-clients width.
    'read_clinical_record',
    'write_clinical_notes',
    'view_all_clinical',
    // Board observer split (0152) — the client-identity write gate (the
    // office five hold it; behavior unchanged for them).
    'manage_client_identity',
    'assign_care_managers',
    'edit_client_details',
    // Team directory private-contact tier (the office keeps the address book).
    'view_staff_private_contact',
    // ADR-052 — Admin's Errors tab: what failed, for whom, and the reference.
    'view_errors',
    // Internal time: her own admin, training and meeting hours.
    'log_internal_time',
    'view_directory_gaps',
  ]),
  owner: new Set<Capability>([
    'view_master_prebilling',
    'read',
    'direct_write',
    'suggest',
    'add_notes',
    'approve',
    'read_all_suggestions',
    'manage_lookups',
    'manage_team',
    'view_errors',
    'manage_staff_access',
    'recommend_provider',
    'delete_provider_note',
    'merge_providers',
    'archive_providers',
    'manage_staff_availability',
    'use_ai_tools',                       // X.3 — Amanda + Trish (co-owner)
    // X.5.1 — hub: all three tiers + announcements management
    'view_staff_tools',
    'view_office_tools',
    'view_exec_tools',
    'manage_announcements',
    // Billing A4 — owner-only compile/approve/issue/void (+ view). Amanda + Trish.
    'compile_invoice',
    'approve_invoice',
    'issue_invoice',
    'void_invoice',
    'view_invoice',
    'view_prebilling',                    // Pre-billing's case-note lines
    'manage_client_rates',                // B1-D3 — set client rates at onboarding
    'set_bill_to',                        // who receives the bill
    // Workspace v1 — full authority incl. checklist-template authoring.
    'view_workspace',
    'manage_work_items',
    'manage_checklist_templates',
    // Card notes — the owner may edit or delete anyone's note.
    'moderate_work_item_notes',
    // Editable workspace processes (0154) — process + stage CRUD.
    'manage_workspace_processes',
    // Workspace v2 Slice 3c — maintain CM bios + photos (People tab).
    'manage_staff_profiles',
    // Clinical width: every client, under either client record setting.
    // The office seats (owner, data_steward, admin_support, exec_support,
    // supervising_cm) hold it by default; primary_cm reads through
    // view_engagement_clients instead.
    'view_all_clinical',
    // Kith clinical record — open to all internal roles (CEO directive):
    // read + write.
    'read_clinical_record',
    'write_clinical_notes',
    // Change or remove a note someone else wrote (the author always may her own).
    'moderate_clinical_notes',
    // Board observer split (0152) — owner holds the client-identity write gate.
    'manage_client_identity',
    'assign_care_managers',
    'edit_client_details',
    // Reports Phase A (#515) — referral-partner analytics; owner + (0152) the
    // read-only board_observer.
    'view_reports',
    // Reports Phase B — weekly operating metrics ingest (0151, owner-only).
    'ingest_operating_metrics',
    // Referral-report split (founder ruling 2026-07-14) — owner holds the
    // referral-analytics read separately from view_reports (see CAPABILITIES
    // comment).
    'view_referral_reports',
    // RBAC Permissions screen (ADR-0046) — owner-only, hard-locked (see the
    // CAPABILITIES comment). The overrides repo refuses any write touching
    // this capability or the owner row, so it can never move off this row.
    'manage_permissions',
    // QBO billing integration (slice 5) — disconnect. Owner only; the
    // sync-status READ rides view_invoice. Connect + reconnect, push and
    // the customer links each ride their own cap, held here too.
    'manage_billing_integration',
    'connect_billing_integration',
    'push_invoice',
    'manage_qbo_customer_links',
    // Team directory private-contact tier (home address, personal phone).
    'view_staff_private_contact',
    // Internal time: her own hours, and the pay-period hours file.
    'log_internal_time',
    'export_payroll_hours',
    'view_directory_gaps',
  ]),
  mso_staff: new Set<Capability>(['read']),
  data_steward: new Set<Capability>([
    'view_master_prebilling',
    'read',
    'direct_write',
    'suggest',
    'add_notes',
    'approve',
    'read_all_suggestions',
    'manage_lookups',
    'recommend_provider',
    'delete_provider_note',
    'merge_providers',
    'archive_providers',
    'manage_staff_availability',
    'use_ai_tools',                       // X.3 — Nicole
    // X.5.1 — hub: all three tiers + announcements management
    'view_staff_tools',
    'view_office_tools',
    'view_exec_tools',
    'manage_announcements',
    'view_invoice',                       // Billing A4 — invoice preview only
    'view_prebilling',                    // Pre-billing's case-note lines
    'manage_client_rates',                // B1-D3 — the bookkeeping seat sets rates
    'set_bill_to',                        // who receives the bill
    // Workspace v1 — client-services owns Client Setup; full authority.
    'view_workspace',
    'manage_work_items',
    'manage_checklist_templates',
    // Editable workspace processes (0154) — process + stage CRUD.
    'manage_workspace_processes',
    // Workspace v2 Slice 3c — maintain CM bios + photos (People tab).
    'manage_staff_profiles',
    // Kith clinical record — open to all internal roles (CEO directive):
    // read + write + all-clients width.
    'read_clinical_record',
    'write_clinical_notes',
    // Change or remove a note someone else wrote (the author always may her own).
    'moderate_clinical_notes',
    'view_all_clinical',
    // Board observer split (0152) — the client-identity write gate (the
    // office five hold it; behavior unchanged for them).
    'manage_client_identity',
    'assign_care_managers',
    'edit_client_details',
    // Referral-report split (founder ruling 2026-07-14) — the bookkeeping
    // seat sees referral-partner analytics without gaining the operating
    // dashboard (view_reports).
    'view_referral_reports',
    // ADR-052 — Admin's Errors tab: what failed, for whom, and the reference.
    'view_errors',
    // Internal time: her own admin, training and meeting hours.
    'log_internal_time',
    'view_directory_gaps',
  ]),
  // Phase X.5.1 — exec_support (Laila, bookkeeper): exec-tier hub + tool
  // access + announcements management WITHOUT data_steward authority (no
  // approve / manage_lookups / merge / etc.).
  exec_support: new Set<Capability>([
    'view_master_prebilling',
    'read',
    // The admin_support floor (founder ruling 2026-08-27). exec_support was
    // built fresh at X.5.1 rather than derived from admin_support, and these
    // three never made the trip. The result was a seat that could issue an
    // invoice but not leave a note, and could not manage staff availability
    // while the administrative seat could. That was a hole, not a narrower
    // seat. Pinned by a strict-superset assertion in
    // __tests__/qboBillingIntegrationCap.test.ts so it cannot reopen one
    // verb at a time.
    'suggest',
    'add_notes',
    'manage_staff_availability',
    'use_ai_tools',
    'view_staff_tools',
    'view_office_tools',
    'view_exec_tools',
    'manage_announcements',
    'view_invoice',                        // Billing A4 — invoice preview only
    'view_prebilling',                    // Pre-billing's case-note lines
    'manage_client_rates',                 // B1-D3 — the bookkeeper sets rates
    'set_bill_to',                         // who receives the bill
    // Workspace v1 — bookkeeper owns the Billing stage; works the board.
    'view_workspace',
    'manage_work_items',
    // Editable workspace processes (0154, founder ruling 2026-07-14) —
    // exec_support gains BOTH process/stage CRUD AND checklist-template item
    // authoring (the latter was previously owner + data_steward only).
    'manage_checklist_templates',
    'manage_workspace_processes',
    // Card notes — exec support may edit or delete anyone's note.
    'moderate_work_item_notes',
    // Workspace v2 Slice 3c — maintain CM bios + photos (People tab).
    'manage_staff_profiles',
    // exec_support onboards and offboards staff: roster add/remove, plus logins.
    'manage_staff_access',
    // Kith clinical record — open to all internal roles (CEO directive):
    // read + write + all-clients width.
    'read_clinical_record',
    'write_clinical_notes',
    // Change or remove a note someone else wrote (the author always may her own).
    'moderate_clinical_notes',
    // Directory notes: remove or change anyone's note, never its anonymity (the same seats that moderate clinical notes).
    'delete_provider_note',
    'view_all_clinical',
    // Board observer split (0152) — the client-identity write gate (the
    // office five hold it; behavior unchanged for them).
    'manage_client_identity',
    'assign_care_managers',
    'edit_client_details',
    // Referral-report split (founder ruling 2026-07-14) — the bookkeeper
    // sees referral-partner analytics without gaining the operating
    // dashboard (view_reports).
    'view_referral_reports',
    // ADR-052 — Admin's Errors tab (the admin support floor carries it).
    'view_errors',
    // Billing operations (founder ruling 2026-08-26: "laila is
    // exec_support - she should be able to issue and push"). The
    // bookkeeper seat now MAKES the document and SENDS it to the books,
    // which is the job it already had everywhere except in Olive.
    // Deliberately WITHOUT manage_billing_integration (disconnecting
    // QuickBooks stays owner-only).
    'issue_invoice',
    'push_invoice',
    // AMENDED 2026-08-27 — THE GRANT ABOVE WAS ONLY THE BACK HALF.
    //
    // An invoice goes draft -> approved -> issued -> pushed, and issueInvoice
    // hard-requires status 'approved' (InvoiceInvalidStateError otherwise).
    // Granting issue + push WITHOUT compile + approve left this seat holding
    // two verbs it could never reach: every month still routed through the
    // owner to MAKE and APPROVE the document, which is exactly the bottleneck
    // the 08-26 ruling set out to remove. The capability was grantable,
    // auditable, visible in the Permissions matrix, and dead.
    //
    // A permission that cannot be exercised is worse than one that is absent:
    // an absent one is a visible gap, a dead one reads as working.
    //
    // Founder ruling 2026-08-27: "Laila should be free to run the whole
    // chain." compile_invoice also carries recompile + discard of a DRAFT;
    // approve_invoice also carries reopen (approved -> draft), the
    // stale-approval recovery path that makes the chain self-healing rather
    // than owner-escalating.
    'compile_invoice',
    'approve_invoice',
    // AMENDED 2026-08-31 — THE CORRECTION PATH JOINS THE CHAIN.
    //
    // void_invoice sat out the 08-26 and 08-27 grants (unmaking an issued
    // document is the undoing of the chain, not a step in it). In practice
    // that made every month-end correction bottleneck on the owner:
    // void-and-reissue is the house correction mechanism (ADR-0029), and
    // the seat that runs the chain at 250 clients must be able to correct
    // it. Founder ruling 2026-08-31: "Yes, definitely." The void dialog
    // names the QuickBooks-copy consequence in plain terms; the copy-check
    // surface holds the operator's hand on the QBO side.
    'void_invoice',
    // Founder ruling 2026-08-26 ("onboarding a new client can't just route
    // through me, it needs to also be exec_support") — the bookkeeper
    // links a new client to its QuickBooks customer. Still WITHOUT
    // manage_billing_integration: she links clients inside a company the
    // owner connected, and cannot connect or disconnect a different one.
    'manage_qbo_customer_links',
    // Connect and RECONNECT QuickBooks, never disconnect it. The connect
    // callback refuses a consent for a different company than the one
    // connected before it spends the code, so this seat can only
    // re-authorize the connected company or connect one when none is;
    // the seat that runs month end brings an expired connection back
    // without waiting on the owner.
    'connect_billing_integration',
    // Team directory private-contact tier (the office keeps the address book).
    'view_staff_private_contact',
    // Internal time: her own hours, and the pay-period hours file the
    // payroll seat feeds to payroll.
    'log_internal_time',
    'export_payroll_hours',
    'view_directory_gaps',
  ]),
  // Board observer (migration 0152; build-specs/board-observer-role.md) —
  // "read EVERYTHING; write ONLY the demo client". A board member who signs a
  // confidentiality agreement: full visibility into every client record
  // (view_all_clinical + read_clinical_record → PHI-audit-logged reads),
  // billing (view_invoice, no compile/approve/issue/void), the reports
  // pages (view_reports), the workspace views, the Peace Hub tool tiers
  // (view_staff_tools/view_office_tools/view_exec_tools — external tool
  // links only, granted 2026-07-14 so the board sees the whole hub), and
  // the suggested-edits queue read (read_all_suggestions, same date).
  //
  // Board demo sandbox (migration 0156, founder ruling 2026-07-15): the
  // board seat now ALSO gets to experience the product hands-on —
  // use_ai_tools (the full AI tools portal) + write_demo_clinical (author
  // case notes / run the note pipeline). Both are DEMO-SCOPED writes: the
  // note-pipeline repo fence (_clinicalWriteGuard) allows a write_demo_clinical
  // caller ONLY against a client flagged is_demo=true, so the board writes
  // the demo client (Frodo) and NOTHING else. Still deliberately WITHOUT
  // write_clinical_notes (full clinical write on any client — stays board-
  // forbidden), manage_client_identity, direct_write, suggest, add_notes,
  // any manage_*, approve, or ingest. Revoke via the People page when a
  // board seat ends (no expiry).
  board_observer: new Set<Capability>([
    'read',
    // Every client's full record — reads flow through withClinicalRead, so
    // every look is PHI-audit-logged (SEC-7 posture unchanged).
    'read_clinical_record',
    'view_all_clinical',
    // Board demo sandbox (0156) — the full AI tools portal + a DEMO-SCOPED
    // clinical write. write_demo_clinical only authors against an is_demo
    // client (the repo fence enforces); use_ai_tools opens every generation
    // tool. Full write_clinical_notes stays board-forbidden.
    'use_ai_tools',
    'write_demo_clinical',
    // Billing visibility only — no compile / approve / issue / void.
    'view_invoice',
    // The reports pages (widened from owner-only to owner + board_observer).
    'view_reports',
    // Referral-report split (founder ruling 2026-07-14) — the read-only
    // board seat sees referral-partner analytics too.
    'view_referral_reports',
    // The board / caseload / conversion views — no manage_work_items.
    'view_workspace',
    // Peace Hub tool tiers (founder ruling 2026-07-14) — the board sees the
    // whole hub; these are read-only external tool links, no write surface.
    'view_staff_tools',
    'view_office_tools',
    'view_exec_tools',
    // The suggested-edits queue read (founder ruling 2026-07-14) — the
    // board can see every pending suggestion; approve/apply stay gated on
    // 'approve', which board_observer does not hold.
    'read_all_suggestions',
  ]),
  // The Directory Editor keeps the directory current: edits organizations
  // and people in place, suggests what she does not do herself (removing a
  // listing, a new listing), leaves and changes her own notes, reads the
  // directory's change log, and logs her own internal time. By default no
  // client record, billing, staff tools or workspace. The client record
  // setting never applies to her (clientRecordAccess.ts
  // NON_CLINICAL_STAFF_ROLES), and her roster rows hold no care flag and no
  // client (repos/_directoryEditorRoster.ts).
  directory_editor: new Set<Capability>([
    'read',
    'direct_write',
    'suggest',
    'add_notes',
    'read_all_suggestions',
    'log_internal_time',
    'view_directory_gaps',
  ]),
  // Accounting (migration 0220): the bookkeepers and accountants. Billing
  // end to end (rates, bill-to, compile through push, the QuickBooks
  // customer links and item map), payroll hours and the reports; every
  // client on the roster (view_all_clinical) for the billing reads, and
  // never a client record, a note, a document or a Directory entry's
  // connected clients (clientRecordAccess.ts CONNECTED_CLIENTS_FENCED_ROLES).
  // Not QuickBooks connect or disconnect, not referral reports.
  accounting: new Set<Capability>([
    'view_master_prebilling',
    'read',
    'view_office_tools',
    'view_invoice',
    'view_all_clinical',
    'compile_invoice',
    'approve_invoice',
    'issue_invoice',
    'void_invoice',
    'push_invoice',
    'manage_client_rates',
    'set_bill_to',
    'manage_qbo_customer_links',
    'export_payroll_hours',
    'view_reports',
  ]),
};

/**
 * Static-config capability check from a role string — the DEFAULT-matrix
 * fallback. Returns false for null/undefined role + for any string
 * outside the matrix (defensive default). Affordance gates prefer the
 * `can*(whoami)` predicates below, which route through
 * `hasEffectiveCapability` so a per-tenant override (ADR-0046) binds.
 */
function hasCapability(role: string | null | undefined, cap: Capability): boolean {
  if (!role) return false;
  const caps = ROLE_CAPABILITIES[role as UserTenantRole];
  if (!caps) return false;
  return caps.has(cap);
}

/**
 * Effective capability check (ADR-0046). The server computes the per-tenant
 * effective set (defaults ± role_capability_overrides) on every request and
 * ships it on `whoami.capabilities`; when present it is authoritative, so
 * owner Permissions-screen changes reach affordance gating without a code
 * deploy. When absent (older cached whoami shape, or a fail-open server
 * path), falls back to the role-derived static default — identical to the
 * pre-ADR-0046 behavior. Every `can*` predicate below routes through this;
 * the predicate SIGNATURES are unchanged, so call sites never churn.
 * Affordance-level only: the server's requireCapability is the boundary.
 */
function hasEffectiveCapability(whoami: WhoamiResponse | null, cap: Capability): boolean {
  if (whoami?.capabilities) return whoami.capabilities.includes(cap);
  return hasCapability(whoami?.role, cap);
}

// =====================================================================
// Predicate exports — one per capability
// =====================================================================
//
// Convention: each accepts a whoami response (or null) and returns true
// when the caller has the capability. Components consume these via
// `useTenant()` to read whoami + pass it in.

export function canRead(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'read');
}

export function canDirectWrite(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'direct_write');
}

export function canSuggest(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'suggest');
}

export function canAddNotes(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'add_notes');
}

export function canApprove(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'approve');
}

export function canReadAllSuggestions(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'read_all_suggestions');
}

export function canManageLookups(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_lookups');
}

export function canManageTeam(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_team');
}

/** Onboard or offboard a person: add/remove them on the roster, and grant, change, or revoke their sign-in and role — owner + exec_support. */
export function canManageStaffAccess(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_staff_access');
}

export function canRecommendProvider(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'recommend_provider');
}

export function canDeleteProviderNote(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'delete_provider_note');
}

/**
 * Whether the person may edit or delete this Directory note: a holder of
 * delete_provider_note any note, an add_notes holder her own (the wire's
 * is_own, the server's id comparison). The server's
 * directoryNoteChangeAllowed behind its delete_provider_note-or-add_notes
 * door (api/_shared/capabilities.ts), one rule for the pencil and the
 * bin on the organization and the person page (LP-37, LP-45).
 */
export function canChangeDirectoryNote(whoami: WhoamiResponse | null, note: { is_own?: boolean }): boolean {
  return canDeleteProviderNote(whoami) || (canAddNotes(whoami) && note.is_own === true);
}

export function canMergeProviders(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'merge_providers');
}

/** Archive and restore a listing, and remove a document someone else
 *  attached: archive_providers, split off direct_write. */
// The Directory map's two Not placed lists (organizations and people the
// map could not place): for the people who fix directory addresses. A
// browser gate only: it declutters the clinical view; the map read still
// carries not_placed for the pins and the search.
export function canViewDirectoryGaps(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_directory_gaps');
}

export function canArchiveProviders(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'archive_providers');
}

// =====================================================================
// Phase X.0 dashboard capabilities (migration 0076) — RETIRED
// =====================================================================
//
// The canViewDashboard*() predicate layer was deleted with the X.1 Dashboard
// page (nav rework 2026-07-13; the operations Dashboard at /reports/operating
// gates on canViewReports instead). The 8 view_dashboard_* capabilities were
// then REMOVED from the CAPABILITIES tuple + ROLE_CAPABILITIES record in the
// 2026-07-18 dead-code cleanup, together with the dashboardMetrics API
// handler (the last server-side consumer). manage_staff_availability is the
// X.0 survivor.

export function canManageStaffAvailability(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_staff_availability');
}

// =====================================================================
// Phase X.3.0 — CM AI Tools Portal capability predicate
// =====================================================================

/**
 * Gates the `/t/:tenantSlug/ai-tools` portal route + the per-tool
 * detail surfaces. Granted to all role tiers EXCEPT mso_staff
 * (cross-tenant; not part of the per-tenant AI Tools surface) and
 * external_accountant (Mowery; AR-only — when that role lands at
 * X.1.3, it explicitly omits this cap). All 15 CMs + the 3 unit ADs
 * + Marisa (SPICE manager) + Amanda + Trish + Nicole + Laila get
 * access — per X.3 brief §3 role-grants table.
 */
export function canUseAiTools(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'use_ai_tools');
}

// =====================================================================
// Phase X.5.1 — Olive hub tile gating + announcements
// =====================================================================
//
// Three tier capabilities gate which hub tiles render:
//   view_staff_tools  — all internal staff (Level 1: CareTree, Resource
//                       Directory, AI Tools, staff SharePoint, Ramp, Rippling).
//   view_office_tools — office + exec (Level 2: Peace Office SP, Bill, QBO,
//                       Chase, Mailchimp, Canva).
//   view_exec_tools   — exec only (Level 3: Peace Executive SP).
// manage_announcements gates the announcements banner edit affordance.

export function canViewStaffTools(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_staff_tools');
}

export function canViewOfficeTools(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_office_tools');
}

export function canViewExecTools(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_exec_tools');
}

export function canManageAnnouncements(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_announcements');
}

/**
 * Hub Prospects (SharePoint) tile — the intake-assignment audience: unit
 * leads (supervising_cm) + data_steward + exec_support + owner.
 *
 * Deliberately role-based (not a matrix capability), per the
 * canViewAllDrafts precedent below: the tile only deep-links to an
 * external SharePoint folder (SharePoint enforces its own access), so a
 * `view_prospects_folder` capability in ROLE_CAPABILITIES would be a
 * server-side dead entry with no enforcing endpoint. No existing tier cap
 * expresses this audience (view_office_tools adds admin_support +
 * board_observer). Centralized here per the §m "no scattered role checks
 * in src/" binding.
 */
const HUB_ASSIGNMENT_TOOL_ROLES: ReadonlySet<string> = new Set<UserTenantRole>([
  'supervising_cm',
  'data_steward',
  'exec_support',
  'owner',
]);

export function canViewAssignmentTools(whoami: WhoamiResponse | null): boolean {
  return whoami?.role != null && HUB_ASSIGNMENT_TOOL_ROLES.has(whoami.role);
}

// =====================================================================
// Billing slice A4 — invoice compile / state machine / issue / preview
// =====================================================================
//
// compile/approve/issue are owner-only (Amanda + Trish co-owner); view is
// also granted to data_steward / admin_support / exec_support for the
// read-only operator preview. Mirrors api/_shared/capabilities.ts.

export function canCompileInvoice(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'compile_invoice');
}

export function canApproveInvoice(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'approve_invoice');
}

export function canIssueInvoice(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'issue_invoice');
}

// BILL-4 — void is a destructive money action; authority is deliberately
// separable from issue (owner-only today, same grant as issue_invoice).
export function canVoidInvoice(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'void_invoice');
}

export function canViewInvoice(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_invoice');
}

// A client's Pre-billing tab: every case note's line for the month. A seat
// without view_invoice sees the lines with no rate or amount.
export function canViewPrebilling(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_prebilling');
}

// The master Pre-billing page (/invoices/prebilling): every client's note
// lines for a date range. The server's gate is the same capability; the
// amount column also needs view_invoice.
export function canViewMasterPrebilling(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_master_prebilling');
}

// B1-D3 (migration 0132) — set/change a client's per-band billing rates
// from the Pre-billing Rates card. Owner + the bookkeeping seats
// (data_steward / exec_support) only; reads ride view_invoice.
export function canManageClientRates(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_client_rates');
}

// Choose who receives the bill: the billing-contact checkbox, the
// Billing details Add and Edit, and Refresh from care contact.
export function canSetBillTo(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'set_bill_to');
}

// =====================================================================
// Workspace v1 (migration 0098) — office + manager work module
// =====================================================================
//
// view_workspace gates the top-nav Workspace pill + the 4 views (office +
// managers only; line CMs have no board — they get bell notifications).
// manage_work_items gates create/edit/move/assign/archive affordances.
// manage_checklist_templates gates template authoring (owner + data_steward).

export function canViewWorkspace(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_workspace');
}

/**
 * A caller reading only their OWN work items — the Hub "My Tasks" rail
 * (HubMyTasksRail.tsx), which asks for ?for_user=<self>. The API admits
 * that self-scoped read to view_staff_tools as well as view_workspace
 * (workItems.ts listWorkItems / listCompletedTasks): every view_workspace
 * role already holds view_staff_tools too, so this is a pure widen — it
 * adds primary_cm/supervising line CMs, who have no Workspace board but
 * do own tasks (the office's birthday/anniversary reminders land here).
 * The rail gates its fetch on this so a caller who fails BOTH capabilities
 * never issues the doomed request.
 */
export function canReadOwnWorkItems(whoami: WhoamiResponse | null): boolean {
  return canViewStaffTools(whoami) || canViewWorkspace(whoami);
}

export function canManageWorkItems(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_work_items');
}

/**
 * A person's own to-do list (LP-2): create, edit, complete, archive and
 * delete the tasks assigned to them, their own recurring tasks, no board.
 * The care managers and SPICE therapists hold it. The list lives on Your
 * day (ClientsHome) for this OR canManageWorkItems, never the Workspace,
 * whose pill stays on canViewWorkspace. The agenda, the Home rail and the
 * drawer show the add box and the per-task actions on this OR
 * canManageWorkItems; the server re-checks the row.
 */
export function canManageOwnTasks(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_own_tasks');
}

export function canManageChecklistTemplates(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_checklist_templates');
}

/** Edit or delete ANYONE's note on a Workspace card. The author of a note
 *  always may their own (manage_work_items covers that); this widens it to
 *  the owner + exec support seats. */
export function canModerateWorkItemNotes(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'moderate_work_item_notes');
}

/** Change or remove a clinical note someone else wrote. The author of a
 *  note always may her own; this widens it to the moderating seats (owner,
 *  data_steward and exec_support by default). Never widens which clients
 *  a person can open. */
export function canModerateClinicalNotes(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'moderate_clinical_notes');
}

// RBAC Permissions screen (ADR-0046, migration 0157) — gates the Admin >
// Permissions tab (owner-only, hard-locked in the overrides repo so the
// gate itself can never be granted away or revoked).
export function canManagePermissions(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_permissions');
}

// Admin console admission: the ONE predicate for whether a user may open
// /admin-review. Consumed by BOTH the AdminReview page gate and the
// 'admin-review' NAV_REGISTRY entry so the two can never disagree (the
// 2026-08-31 report: exec_support passed the page gate via
// manage_staff_profiles but the nav entry only checked the first two
// capabilities, leaving an authorized seat with no link to the screen).
// Any widening of the page gate must happen HERE, not inline at either
// consumer; tests/ui/navRegistryCoverage.test.tsx pins the parity.
export function canOpenAdminConsole(whoami: WhoamiResponse | null): boolean {
  return (
    canManageLookups(whoami) ||
    canReadAllSuggestions(whoami) ||
    canManageStaffProfiles(whoami) ||
    canManagePermissions(whoami) ||
    // The billing seats: Billing > Client rates and Billing > QuickBooks.
    // Every other tab hides by its own capability (AdminReviewTabs).
    canManageClientRates(whoami) ||
    canManageQboCustomerLinks(whoami)
  );
}

// QBO billing integration (QBO API v2 slice 5) — gates the Disconnect
// control only (owner-only). Connect and Reconnect ask
// canConnectBillingIntegration, the push asks canPushInvoice, and the
// sync-status READ rides view_invoice so the office can see a failed
// push without being able to cause one.
export function canManageBillingIntegration(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_billing_integration');
}

// Connecting QuickBooks, and reconnecting it when the credential has
// expired. A DIFFERENT question from canManageBillingIntegration: the
// bookkeeper seat may bring the connection back and may not revoke it.
// The connect callback refuses a consent for a different company than
// the one connected, so this control can only re-authorize the connected
// company or connect one when none is.
export function canConnectBillingIntegration(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'connect_billing_integration');
}

// Sending an ISSUED invoice to QuickBooks (founder ruling 2026-08-26).
// Deliberately a DIFFERENT question from canManageBillingIntegration: the
// bookkeeper seat pushes, the owner seat connects. A surface that asks the
// wrong one either hides the batch from the person who runs month end or
// shows Disconnect to someone who must not have it.
export function canPushInvoice(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'push_invoice');
}

// Matching a client to a QuickBooks customer, and unmatching one (founder
// ruling 2026-08-26). A THIRD question, distinct from both neighbours:
// the owner connects the company, the bookkeeper links clients inside it
// and sends their invoices.
export function canManageQboCustomerLinks(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_qbo_customer_links');
}

// Admission to the QuickBooks settings view (Admin > Billing >
// QuickBooks) and to the Invoices rail's link into it. The UNION of the
// three write questions the view's contents ask: the owner disconnects
// the company, the billing seats connect or reconnect it, and they link
// clients inside it and map the rate bands to QuickBooks items. Any one
// answer alone is enough to have work in there; none means the view
// holds nothing the seat may do, and its own failed pushes point
// somewhere else.
export function canOpenQuickBooksSettings(whoami: WhoamiResponse | null): boolean {
  return (
    canManageBillingIntegration(whoami) ||
    canConnectBillingIntegration(whoami) ||
    canManageQboCustomerLinks(whoami)
  );
}

// Editable workspace processes (migration 0154) — process + stage CRUD
// (multi-process kanbans). owner + data_steward + exec_support (founder
// ruling 2026-07-14). Gates the board edit-mode pencil + the process editor.
export function canManageWorkspaceProcesses(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_workspace_processes');
}

// Workspace v2 Slice 3c — edit a CM's bio + photo in the People tab. Broader
// than manage_team (owner-only): owner + admin_support + data_steward +
// exec_support. The photo SERVE stays at the view_workspace floor.
export function canManageStaffProfiles(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_staff_profiles');
}

// Team directory — the private-contact tier (home address, personal phone):
// owner + admin_support + exec_support. The server nulls the two columns for
// every other seat; this predicate only decides whether the editor renders
// the fields at all.
export function canViewStaffPrivateContact(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_staff_private_contact');
}

// Internal time: the viewer logs her own admin, training and meeting hours
// (the "+ Internal Time" door on her home). Every roster person with a login
// holds it by default; the external and board seats do not.
export function canLogInternalTime(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'log_internal_time');
}

// The pay-period hours file the office feeds to payroll: owner + the
// payroll seat by default, grantable to others on the Permissions screen.
export function canExportPayrollHours(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'export_payroll_hours');
}

// All-clients clinical width (ADR-043): the office seats hold it and read
// every client under either client record setting. Server-side it drives
// the app.clinical_scope GUC. A line clinician's width under Everyone on
// staff is canReadEveryClient, not this.
export function canViewAllClinical(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_all_clinical');
}

// The engagement lane (ADR-0053): the capability alone says nothing; the
// viewer's roster flags (whoami.does_care_management / does_spice) name the
// lane. This predicate is the capability half only.
export function canViewEngagementClients(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_engagement_clients');
}

// Does this viewer read every client? The browser twin of the server's
// readsEveryClient: the office width, or the engagement lane held by an
// internal role when the tenant's client record setting is all_staff
// (whoami.client_record_access; absent reads as own_service). The role
// check is the internal allow-list, so an external seat given the lane
// never reads every client from the setting. The browser's only reader of
// the setting: every roster view, client lookup and connected-clients
// section that follows the width asks this.
export function canReadEveryClient(whoami: WhoamiResponse | null): boolean {
  return (
    canViewAllClinical(whoami) ||
    (canViewEngagementClients(whoami) &&
      whoami?.client_record_access === 'all_staff' &&
      isInternalStaffRole(whoami?.role))
  );
}

// May this viewer follow a link into any client's record (and the care
// manager view, a list of record links)? The office width AND the record
// itself. Every role but accounting that holds view_all_clinical also holds
// read_clinical_record; the billing seat reads every client on the roster
// for billing and opens no record, so a record link would land it on the
// refusal page.
export function canOpenEveryClientRecord(whoami: WhoamiResponse | null): boolean {
  return canViewAllClinical(whoami) && canReadClinicalRecord(whoami);
}

// May this viewer see a Directory entry's connected clients? Every client
// (canReadEveryClient), unless the role is fenced from them: the billing
// seat reads the roster for billing only. The server refuses the same
// roles with 403 connected_clients_not_available.
export function canSeeConnectedClients(whoami: WhoamiResponse | null): boolean {
  return canReadEveryClient(whoami) && !isConnectedClientsFenced(whoami?.role);
}

// May this viewer open the all-clients roster (Clients page, the "All
// clients" links, the landing redirect)? Either every client
// (canReadEveryClient), or the engagement lane with at least one roster
// flag to name it. A view_engagement_clients holder with neither flag, under
// Each person's own service, is care-team-scoped and the roster stays
// closed, the same answer the server gives that request.
export function canViewAnyClientRoster(whoami: WhoamiResponse | null): boolean {
  return (
    canReadEveryClient(whoami) ||
    (canViewEngagementClients(whoami) &&
      ((whoami?.does_care_management ?? false) || (whoami?.does_spice ?? false)))
  );
}

/** Admin's Errors tab (ADR-052): the owner and admin support. */
export function canViewErrors(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_errors');
}

// Kith clinical record surface — open to all internal roles per the CEO
// "keep permissions open" directive. read gates opening a client's clinical
// record surface; write gates authoring into it. Fine-grained per-role
// restriction lands later via a runtime owner Permissions screen.
export function canReadClinicalRecord(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'read_clinical_record');
}

export function canWriteClinicalNotes(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'write_clinical_notes');
}

// Board demo sandbox (migration 0156) — a DEMO-SCOPED clinical write cap
// held ONLY by board_observer. The server is the boundary: the note-pipeline
// repo fence allows a write_demo_clinical caller to author ONLY against an
// is_demo client. This predicate drives the FE affordance so the note-
// authoring doors (LogAsCaseNote, AssessmentDrafter file-to-record) go LIVE
// for board, with the client pickers filtered to demo clients as a nicety.
export function canWriteDemoClinical(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'write_demo_clinical');
}

// True when the caller may author clinical notes at all — full clinical
// write OR the demo-scoped sandbox write. The note-authoring affordances
// gate on this; demo-only callers get their pickers filtered to is_demo
// clients (the server enforces the scope regardless).
export function canAuthorClinicalNotes(whoami: WhoamiResponse | null): boolean {
  return canWriteClinicalNotes(whoami) || canWriteDemoClinical(whoami);
}

// True when the caller may change this note: she may author notes at all,
// and she wrote it or she moderates notes. Every door that changes a note
// (Edit, Remove, the invoice line, Mark as ready and its Undo, the hours
// and band) gates on this; the server's repo guard is the boundary.
export function canChangeNote(whoami: WhoamiResponse | null, authorUserId: string | null | undefined): boolean {
  if (!canAuthorClinicalNotes(whoami)) return false;
  if (authorUserId != null && whoami?.user_id === authorUserId) return true;
  return canModerateClinicalNotes(whoami);
}

// write_clinical_notes is held broadly (any staffer who posts notes, SPICE
// therapists included) — it says nothing about who keeps the STRUCTURED
// record (allergies, conditions, medications, and the rest of the cards
// under Care record / Life & logistics / People). A SPICE-only therapist
// posts session notes but never touches that record: does_care_management
// (staff_roster) names her as the person who does, and view_all_clinical
// names the office width that stands in for it. The card-level write
// affordances (+ Add, From a photo, the allergy NKDA doors) gate on this
// instead of plain write_clinical_notes so a SPICE-only viewer reads the
// record's facts with none of the doors that presume she keeps it.
export function canWriteClinicalRecord(whoami: WhoamiResponse | null): boolean {
  return (
    canWriteClinicalNotes(whoami) &&
    ((whoami?.does_care_management ?? false) || canViewAllClinical(whoami))
  );
}

// =====================================================================
// Board observer (migration 0152) — the client-identity write split
// =====================================================================
//
// manage_client_identity gates the client-identity WRITE affordances that
// used to ride view_all_clinical (Rename, Change status, pronouns, the
// Assign/Change CM strip, the About/Contact edits, the client photo, and
// the billing-contact Refresh). The five office roles hold it; a
// board_observer holds view_all_clinical (so the record OPENS and reads)
// but NOT this — so the edit affordances hide and the pages read clean.
export function canManageClientIdentity(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'manage_client_identity');
}

// A client's everyday details (phone, email, preferred name, pronouns,
// photo, relationship status, referred by, the service and its SPICE
// therapist, SPICE staff and the SPICE schedule): edit_client_details or
// the office's manage_client_identity. The server's twin is
// canEditClientDetails in api/_shared/capabilities.ts.
export function canEditClientDetails(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'edit_client_details') || hasEffectiveCapability(whoami, 'manage_client_identity');
}

// The care-manager doors on a client (N4): change the lead, add or remove
// another. Either the whole client-identity write or the narrow
// assign_care_managers opens them; the SPICE therapist doors stay on
// canManageClientIdentity. The list behind the doors (assign-options)
// admits every seat this admits, and tests/ui/assignCareManagersGate.test.ts
// keeps the two equal over every capability set. Only a care manager
// assigns care managers: a seat whose way in is assign_care_managers needs
// its own roster row's does_care_management (whoami, from the same reader
// the server's gate uses); an absent flag reads as not a care manager.
export function canAssignCareManagers(whoami: WhoamiResponse | null): boolean {
  return (
    hasEffectiveCapability(whoami, 'manage_client_identity') ||
    (hasEffectiveCapability(whoami, 'assign_care_managers') && whoami?.does_care_management === true)
  );
}

// The address check before a save (POST /address-check) is open to every
// seat that edits an address: one that writes clinical notes, suggests
// Directory edits or sets who receives the bill. Every address form asks this before it sends a check,
// so a seat the server would refuse never draws "could not be checked";
// tests/ui/canCheckAddressParity.test.ts keeps it equal to the server's
// gate over every role.
export function canCheckAddress(whoami: WhoamiResponse | null): boolean {
  return (
    hasEffectiveCapability(whoami, 'write_clinical_notes')
    || hasEffectiveCapability(whoami, 'suggest')
    || hasEffectiveCapability(whoami, 'set_bill_to')
  );
}

// A client's SPICE schedule, its status (on hold, vacation until, in
// hospital, back to active) and its cover are the client's availability.
// Changing any of it is an everyday detail (canEditClientDetails): every
// internal seat changes it on a client it may open. Every door
// onto a client's schedule reads this one predicate. The server's
// canEditClientSchedule (api/_shared/capabilities.ts) and the record
// aggregate's vitals.canEditSpiceSchedule answer the same.
export function canEditClientSchedule(whoami: WhoamiResponse | null): boolean {
  return canEditClientDetails(whoami);
}

// Reports Phase A (#515) — referral-partner analytics. Gates the Reports nav
// pill + the /reports/referrals page. Owner at launch; widened to owner +
// board_observer by 0152 (the read-only board seat sees the reports pages).
// Frontend-only predicate — the byte-mirror discipline covers the
// CAPABILITIES tuple + ROLE_CAPABILITIES record, not the predicates below.
export function canViewReports(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_reports');
}

// Referral-report split (founder ruling 2026-07-14) — gates the
// /reports/referrals referral-partner analytics SEPARATELY from view_reports
// (the operating dashboard), so data_steward + exec_support see the
// referral analytics without gaining the operating dashboard. Granted to
// owner, board_observer, data_steward, exec_support.
export function canViewReferralReports(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'view_referral_reports');
}

// Reports Phase B — weekly operating metrics ingest (0151). Gates the
// owner-only upload door on /reports/operating (the paste-JSON / CSV card
// that POSTs the Monday routine's payload). Owner-only, SEPARATE from
// view_reports so the write gate never widens with the read gate.
export function canIngestOperatingMetrics(whoami: WhoamiResponse | null): boolean {
  return hasEffectiveCapability(whoami, 'ingest_operating_metrics');
}

/**
 * X.3.4.11 — owner-only "show all drafts" toggle in the drafts inbox.
 *
 * Deliberately role-based (not a matrix capability): the server gates the
 * cross-user draft view on `ctx.role === 'owner'` directly in
 * listDraftsForUser (own-only for everyone else, re-derived server-side),
 * so adding a `view_all_drafts` capability to ROLE_CAPABILITIES would be
 * a server-side dead entry. This predicate is the affordance mirror — it
 * only decides whether the toggle renders; the server is the boundary.
 * Centralized here per the §m "no scattered role checks in src/" binding.
 */
export function canViewAllDrafts(whoami: WhoamiResponse | null): boolean {
  return whoami?.role === 'owner';
}

/** The Bake Off League's owner box (results and the draft). Render only:
 *  the server re-derives the owner on every write. */
export function canRunBakeOff(whoami: WhoamiResponse | null): boolean {
  return whoami?.role === 'owner';
}

// =====================================================================
// Backward-compat aliases (Phase 1.11 — Era B may delete)
// =====================================================================
//
// The pre-Phase-1.11 helpers `canOwn(whoami)` / `canAddLookup(whoami)`
// consumed hardcoded role lists. They're preserved here as backward-compat
// aliases over the capability predicates so existing call sites (TeamTab /
// LookupPicker) don't churn in the Phase 1.11 PR. New code should consume
// the capability predicate directly.

/**
 * @deprecated Phase 1.11 — use `canManageTeam(whoami)` instead. Today
 * 1:1 with `canManageTeam` (only owner has both); preserved as alias for
 * existing AppShell + Team-tab consumer sites.
 */
export function canOwn(whoami: WhoamiResponse | null): boolean {
  return canManageTeam(whoami);
}
